import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { ServiceOrderService } from 'src/shared/services/service-order.service';
import { Unsubscriber } from '../../unsubscriber/unsubscriber.component';
import { Installation, User, mapUserNode, toFloat } from '../models/service-order.model';
import { ServiceOrderInstallationPanelComponent } from '../installation-panel/service-order-installation-panel.component';
import { PRIORITY_OPTIONS, SelectOption, buildSelectOptions, priorityMeta } from '../models/service-order.labels';

/** Garante que o autocomplete devolveu um objeto selecionado, e não o texto digitado **/
function selectedObject(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  return value && typeof value === 'object' && value.id ? null : { notSelected: true };
}

@Component({
  selector: 'app-service-order-create-dialog',
  templateUrl: './service-order-create-dialog.component.html',
  styleUrls: ['./service-order-create-dialog.component.less'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    AutoCompleteModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    TextareaModule,
    TooltipModule,
    ServiceOrderInstallationPanelComponent,
  ],
})
export class ServiceOrderCreateDialogComponent extends Unsubscriber {
  constructor(
    private formBuilder: FormBuilder,
    private serviceOrderService: ServiceOrderService,
    private errorLibService: ErrorLibService,
    private translate: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {
    super();

    this.buildSelectOptions();
    // Os labels são traduzidos na montagem das opções, então trocar de idioma precisa refazê-las
    this.anotherSubscription = this.translate.onLangChange.subscribe(() => this.buildSelectOptions());
  }

  private buildSelectOptions(): void {
    this.priorityOptions = buildSelectOptions(PRIORITY_OPTIONS, priorityMeta, (key) => this.translate.instant(key));
  }

  @Output() closed = new EventEmitter<void>();

  /** Referência da OS criada (informada ou gerada pelo servidor) **/
  @Output() created = new EventEmitter<string | null>();

  public visible: boolean = true;

  public createLoading: boolean = false;

  public installationFilterLoading: boolean = false;

  public userFilterLoading: boolean = false;

  public installationOptions: Installation[] = [];

  /**
   * Confirmação de descarte inline, no próprio rodapé. Era um Swal, mas confirmação em
   * overlay sobre um modal depende de z-index de duas bibliotecas diferentes: quando ela
   * não aparece, a promise nunca resolve e o modal fica preso aberto.
   **/
  public discardConfirmOpen: boolean = false;

  public userOptions: User[] = [];

  public priorityOptions: SelectOption[] = [];

  /**
   * Status não é escolhido na criação: uma OS nova é por definição aberta, e o import em
   * lote também cria sempre como OPEN no alerts-api.
   **/
  public form: FormGroup = this.formBuilder.group({
    // Opcional: vazio, o alerts-api gera DDMMYY + sequência do dia (ex.: 100926001)
    reference: [null],
    selectedInstallation: [null, [Validators.required, selectedObject]],
    problems: [null],
    notes: [null],
    priority: ['MEDIUM', Validators.required],
    selectedUser: [null, [Validators.required, selectedObject]],
  });

  /** Instalação escolhida (objeto), ou null enquanto o campo tem só texto digitado **/
  public get selectedInstallation(): Installation | null {
    const value = this.form.get('selectedInstallation')?.value;

    return value && typeof value === 'object' && value.id ? (value as Installation) : null;
  }

  // Busca instalações para o autocomplete
  public searchInstallation(event: { query: string }): void {
    this.installationFilterLoading = true;

    // Referência da instalação ou serial do equipamento: o serviço resolve os dois
    this.anotherSubscription = this.serviceOrderService
      .searchInstallations(event?.query)
      .subscribe({
        next: (installations: Installation[]) => {
          this.installationOptions = installations;
          this.installationFilterLoading = false;
        },
        error: (error: any) => {
          this.installationFilterLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  // Busca usuários para o autocomplete de técnico responsável
  public searchUser(event: { query: string }): void {
    this.userFilterLoading = true;

    this.anotherSubscription = this.serviceOrderService
      .searchUsers(event?.query)
      .subscribe({
        next: (users: User[]) => {
          this.userOptions = users;
          this.userFilterLoading = false;
        },
        error: (error: any) => {
          this.userFilterLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  public createServiceOrder(): void {
    if (!this.form.valid) {
      return;
    }

    this.createLoading = true;

    const value = this.form.value;
    const installation: Installation | null = value?.selectedInstallation ?? null;

    this.anotherSubscription = this.serviceOrderService
      .createServiceOrder({
        reference: value?.reference?.trim() || null,
        installationId: installation?.id ?? null,
        installationReference: installation?.reference ?? null,
        status: 'OPEN',
        priority: value?.priority,
        latitude: toFloat(installation?.latitude),
        longitude: toFloat(installation?.longitude),
        deviceMac: installation?.deviceMac ?? null,
        assignee: value?.selectedUser?.id ?? null,
        problems: value?.problems || null,
        notes: value?.notes || null,
      })
      .subscribe({
        next: (response: any) => {
          this.createLoading = false;

          // A tela avisa em toast com a referência que voltou do servidor (gerada ou informada)
          const created: string | null = response?.data?.serviceOrderCreate?.serviceOrder?.reference
            ?? value?.reference?.trim()
            ?? null;

          this.created.emit(created);
          this.close();
        },
        error: (error: any) => {
          this.createLoading = false;
          this.errorLibService.errorAlert(error);
        },
      });
  }

  /** Clique na máscara deste dialog: mesmo caminho do Cancelar (ver comentário no template) **/
  @HostListener('document:mousedown', ['$event'])
  public onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (this.visible && target?.classList?.contains('p-dialog-mask') && this.host.nativeElement.contains(target)) {
      this.cancel();
    }
  }

  /** ESC: ignorado enquanto um overlay do autocomplete ou um Swal estiver aberto **/
  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (!this.visible || document.querySelector('.p-autocomplete-overlay, .swal2-container')) {
      return;
    }

    if (this.discardConfirmOpen) {
      this.dismissDiscard();
      return;
    }

    this.cancel();
  }

  /** Cancelar/fechar: pede confirmação no rodapé quando há alteração pendente **/
  public cancel(): void {
    if (!this.form.dirty) {
      this.close();
      return;
    }

    this.discardConfirmOpen = true;
    this.visible = true;
  }

  /** Confirma o descarte e fecha **/
  public confirmDiscard(): void {
    this.discardConfirmOpen = false;
    this.close();
  }

  /** Desiste de descartar e volta ao formulário **/
  public dismissDiscard(): void {
    this.discardConfirmOpen = false;
  }

  private close(): void {
    this.visible = false;
    this.closed.emit();
  }

  /**
   * Fechamento disparado pelo próprio p-dialog (ESC, X, clique fora). O clique na máscara vem de
   * um listener registrado pelo PrimeNG fora do ciclo de eventos do template e não dispara a
   * detecção de mudanças sozinho; markForCheck garante que o pai reavalie o @if do modal.
   **/
  public onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.visible = true;
      this.cancel();
      this.changeDetector.markForCheck();
    }
  }
}
